/**
 * Reine Hilfsfunktionen der Voice-UI (ohne React, ohne Netzwerk) – einzeln testbar.
 * Medien-Parameter (Bitrate, Bildschirm-Presets) kommen aus der Join-Antwort (docs/API.md 4.6).
 */
import {
  SCREENSHARE_PRESETS,
  allowedScreensharePresets,
  clampScreensharePreset,
  screensharePresetSpec,
  type ScreensharePreset,
  type VoiceJoinDto,
  type VoiceParticipantMetadata,
  type VoiceStateDto,
  type VoiceStatePatchInput,
} from '@lagerfeuer/shared';
import { Track, type RoomOptions, type ScreenShareCaptureOptions, type TrackPublishOptions } from 'livekit-client';

// ---------- Typen ----------

export type VoiceStatus = 'idle' | 'connecting' | 'connected' | 'reconnecting';
export type QualityLevel = 'excellent' | 'good' | 'poor' | 'lost' | 'unknown';
export type VoiceDeviceKind = 'audioinput' | 'videoinput' | 'audiooutput';
export type DevicePrefs = Partial<Record<VoiceDeviceKind, string>>;

/** Vollständiger, gemeldeter Sprachstatus (Gegenstück zu `PATCH /voice/state`). */
export interface ReportedVoiceState {
  selfMute: boolean;
  selfDeaf: boolean;
  video: boolean;
  streaming: boolean;
}

// ---------- Speicher (localStorage, immer mit try/catch) ----------

export const VOLUME_STORAGE_KEY = 'lf.voice.volumes';
export const DEVICE_STORAGE_KEY = 'lf.voice.devices';
export const PRESET_STORAGE_KEY = 'lf.voice.screenPreset';

/** Liefert `window.localStorage`, falls nutzbar (Privatmodus/SSR/Tests: null). */
export function safeStorage(): Storage | null {
  try {
    return typeof window !== 'undefined' && window.localStorage ? window.localStorage : null;
  } catch {
    return null;
  }
}

function readJson(storage: Storage | null, key: string): unknown {
  if (!storage) return null;
  try {
    const raw = storage.getItem(key);
    return raw ? (JSON.parse(raw) as unknown) : null;
  } catch {
    return null;
  }
}

function writeJson(storage: Storage | null, key: string, value: unknown): void {
  if (!storage) return;
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch {
    // Speicher voll oder gesperrt – Einstellung gilt dann nur für diese Sitzung.
  }
}

/** Lautstärke je Teilnehmer auf 0 … 1 begrenzen (ungültig → 1). */
export function clampVolume(v: unknown): number {
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return 1;
  return Math.min(1, Math.max(0, n));
}

export function loadVolumes(storage: Storage | null = safeStorage()): Record<string, number> {
  const raw = readJson(storage, VOLUME_STORAGE_KEY);
  const out: Record<string, number> = {};
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return out;
  for (const [id, v] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof v === 'number' && Number.isFinite(v)) out[id] = clampVolume(v);
  }
  return out;
}

/** Speichert eine Lautstärke; 100 % wird nicht gespeichert (Standard). Liefert die neue Tabelle. */
export function saveVolume(userId: string, volume: number, storage: Storage | null = safeStorage()): Record<string, number> {
  const all = loadVolumes(storage);
  const v = clampVolume(volume);
  if (v === 1) delete all[userId];
  else all[userId] = v;
  writeJson(storage, VOLUME_STORAGE_KEY, all);
  return all;
}

export function loadDevicePrefs(storage: Storage | null = safeStorage()): DevicePrefs {
  const raw = readJson(storage, DEVICE_STORAGE_KEY);
  const out: DevicePrefs = {};
  if (!raw || typeof raw !== 'object') return out;
  for (const kind of ['audioinput', 'videoinput', 'audiooutput'] as const) {
    const v = (raw as Record<string, unknown>)[kind];
    if (typeof v === 'string' && v) out[kind] = v;
  }
  return out;
}

export function saveDevicePref(kind: VoiceDeviceKind, deviceId: string, storage: Storage | null = safeStorage()): DevicePrefs {
  const prefs = { ...loadDevicePrefs(storage), [kind]: deviceId };
  writeJson(storage, DEVICE_STORAGE_KEY, prefs);
  return prefs;
}

export function loadScreenPreset(storage: Storage | null = safeStorage()): string | null {
  const raw = readJson(storage, PRESET_STORAGE_KEY);
  return typeof raw === 'string' ? raw : null;
}

export function saveScreenPreset(preset: ScreensharePreset, storage: Storage | null = safeStorage()): void {
  writeJson(storage, PRESET_STORAGE_KEY, preset);
}

// ---------- Medien-Optionen ----------

/** ICE-Server der Join-Antwort als RTCConfiguration; leer → undefined (dann gelten die Server-Vorgaben von LiveKit). */
export function rtcConfigFrom(iceServers: VoiceJoinDto['iceServers'] | null | undefined): RTCConfiguration | undefined {
  const list = (iceServers ?? []).filter((s) => Array.isArray(s.urls) && s.urls.length > 0);
  if (list.length === 0) return undefined;
  return {
    iceServers: list.map((s) => ({
      urls: s.urls,
      ...(s.username !== undefined ? { username: s.username } : {}),
      ...(s.credential !== undefined ? { credential: s.credential } : {}),
    })),
  };
}

/** Bildschirm-Encoding für ein Preset (Bitrate/fps aus `screensharePresetSpec`). */
export function screenShareEncodingFor(preset: ScreensharePreset) {
  const spec = screensharePresetSpec(preset);
  return { maxBitrate: spec.maxBitrate, maxFramerate: spec.fps };
}

/**
 * Raum-Optionen: adaptiveStream/dynacast, hohe Audio-Bitrate aus der Join-Antwort (ohne DTX, mit RED),
 * Mikrofon mono (Sprache), gewählte Geräte, Bildschirm-Encoding für das gewählte Preset.
 */
export function buildRoomOptions(join: Pick<VoiceJoinDto, 'bitrate'>, prefs: DevicePrefs, preset: ScreensharePreset): RoomOptions {
  const bitrate = Math.max(8_000, Math.round(join.bitrate || 0) || 96_000);
  return {
    adaptiveStream: true,
    dynacast: true,
    disconnectOnPageLeave: true,
    stopLocalTrackOnUnpublish: true,
    audioCaptureDefaults: {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
      ...(prefs.audioinput ? { deviceId: prefs.audioinput } : {}),
    },
    videoCaptureDefaults: {
      resolution: { width: 1280, height: 720, frameRate: 30 },
      ...(prefs.videoinput ? { deviceId: prefs.videoinput } : {}),
    },
    audioOutput: prefs.audiooutput ? { deviceId: prefs.audiooutput } : undefined,
    publishDefaults: {
      audioPreset: { maxBitrate: bitrate },
      dtx: false,
      red: true,
      forceStereo: false,
      stopMicTrackOnMute: false,
      videoEncoding: { maxBitrate: 1_700_000, maxFramerate: 30 },
      screenShareEncoding: screenShareEncodingFor(preset),
    },
  };
}

/** Aufnahme-Optionen fürs Bildschirmteilen (Auflösung/fps des Presets, Systemaudio, wenn gewünscht). */
export function screenShareCaptureOptions(preset: ScreensharePreset, withAudio: boolean): ScreenShareCaptureOptions {
  const spec = screensharePresetSpec(preset);
  return {
    video: true,
    audio: withAudio ? { echoCancellation: false, noiseSuppression: false, autoGainControl: false, channelCount: 2 } : false,
    systemAudio: withAudio ? 'include' : 'exclude',
    resolution: { width: spec.width, height: spec.height, frameRate: spec.fps },
    // 60 fps: Bewegung (Spiele/Videos) bevorzugen, sonst Schärfe für Text.
    contentHint: spec.fps >= 60 ? 'motion' : 'detail',
    selfBrowserSurface: 'exclude',
    surfaceSwitching: 'include',
  };
}

/**
 * Bitrate des Bildschirmtons: mindestens 128 kbit/s (Stereo), aber nie über dem Admin-Limit `maxAudioBitrate`
 * (Join-Antwort; ≤ 0/ungültig = kein Limit bekannt).
 */
export function screenShareAudioBitrate(audioBitrate: number, maxAudioBitrate?: number | null): number {
  const wanted = Math.max(128_000, Number.isFinite(audioBitrate) ? audioBitrate : 0);
  const limit = typeof maxAudioBitrate === 'number' && Number.isFinite(maxAudioBitrate) && maxAudioBitrate > 0 ? maxAudioBitrate : Infinity;
  return Math.min(limit, wanted);
}

/** Veröffentlichungs-Optionen fürs Bildschirmteilen; Systemaudio in Stereo mit hoher Bitrate (gedeckelt). */
export function screenSharePublishOptions(preset: ScreensharePreset, audioBitrate: number, maxAudioBitrate?: number | null): TrackPublishOptions {
  return {
    screenShareEncoding: screenShareEncodingFor(preset),
    audioPreset: { maxBitrate: screenShareAudioBitrate(audioBitrate, maxAudioBitrate) },
    dtx: false,
    red: false,
    forceStereo: true,
  };
}

/** Wählbare Presets bis zum Admin-Limit plus das wirksame (gedeckelte) Preset. */
export function screenPresetChoice(maxPreset: string | null | undefined, wanted: string | null | undefined) {
  const max = maxPreset && (SCREENSHARE_PRESETS as readonly string[]).includes(maxPreset) ? maxPreset : SCREENSHARE_PRESETS[0];
  return { allowed: allowedScreensharePresets(max), preset: clampScreensharePreset(wanted, max) };
}

export function presetLabel(preset: ScreensharePreset): string {
  const spec = screensharePresetSpec(preset);
  return `${spec.height}p · ${spec.fps} fps`;
}

// ---------- Zustand ----------

/** Unterschied zwischen gemeldetem und gewünschtem Status (null = nichts zu melden). */
export function diffVoiceState(prev: ReportedVoiceState | null, next: ReportedVoiceState): VoiceStatePatchInput | null {
  const out: VoiceStatePatchInput = {};
  for (const k of ['selfMute', 'selfDeaf', 'video', 'streaming'] as const) {
    if (!prev || prev[k] !== next[k]) out[k] = next[k];
  }
  return Object.keys(out).length > 0 ? out : null;
}

/** VOICE_STATE_UPDATE in die Tabelle „Kanal → Zustände" einarbeiten (Nutzer ist höchstens in einem Kanal). */
export function applyVoiceStateUpdate(
  byChannel: Record<string, VoiceStateDto[]>,
  update: VoiceStateDto & { leftChannelId?: string | null },
): Record<string, VoiceStateDto[]> {
  const out: Record<string, VoiceStateDto[]> = {};
  for (const [ch, list] of Object.entries(byChannel)) out[ch] = list.filter((s) => s.userId !== update.userId);
  if (update.channelId) {
    const { leftChannelId: _left, ...state } = update;
    out[update.channelId] = [...(out[update.channelId] ?? []), state];
  }
  return out;
}

/** Eigenes VOICE_STATE_UPDATE (Socket) mit den additiven Feldern aus docs/API.md 4.6. */
export type VoiceStateUpdate = VoiceStateDto & { leftChannelId?: string | null; moved?: boolean };

/**
 * Erkennt am eigenen VOICE_STATE_UPDATE ein Verschieben durch einen Moderator: `moved === true`, neuer Kanal ≠
 * bisheriger und `leftChannelId` = bisheriger Kanal. Liefert den Zielkanal, sonst null – ohne `moved` ist es ein
 * eigener Beitritt auf einem anderen Gerät/Tab, dem dieser Client NICHT folgen darf.
 */
export function voiceMoveTarget(
  update: Pick<VoiceStateDto, 'channelId'> & { leftChannelId?: string | null; moved?: boolean },
  currentChannelId: string | null,
): string | null {
  if (update.moved !== true || !currentChannelId || !update.channelId || update.channelId === currentChannelId) return null;
  return update.leftChannelId === currentChannelId ? update.channelId : null;
}

/** Eigener VoiceState im Kanal (oder null). */
export function ownVoiceState(byChannel: Record<string, VoiceStateDto[]>, channelId: string | null, userId: string | null): VoiceStateDto | null {
  if (!channelId || !userId) return null;
  return byChannel[channelId]?.find((s) => s.userId === userId) ?? null;
}

/** Wiedergabe stumm: selbst taub ODER vom Server taubgeschaltet. */
export function playbackMuted(selfDeaf: boolean, own: Pick<VoiceStateDto, 'serverDeaf'> | null | undefined): boolean {
  return selfDeaf || !!own?.serverDeaf;
}

export interface PublishRights {
  canPublishAudio: boolean;
  canPublishVideo: boolean;
  canScreenshare: boolean;
  /** Bildschirmton (nicht bei Server-Mute/-Deaf) – sonst wird der Bildschirm ohne Ton geteilt. */
  canScreenshareAudio: boolean;
}

/**
 * Veröffentlichungsrechte aus LiveKit-`ParticipantPermission` (Token-Grant bzw. serverseitige Änderung):
 * canPublish=false → nichts; leere Quellenliste bei canPublish=true → alles erlaubt; sonst nur die gelisteten Quellen
 * (Protobuf-`TrackSource`). Ohne Rechte-Objekt: null (unverändert lassen).
 */
export function publishRightsFrom(perm: { canPublish?: boolean; canPublishSources?: readonly number[] } | null | undefined): PublishRights | null {
  if (!perm) return null;
  if (!perm.canPublish) return { canPublishAudio: false, canPublishVideo: false, canScreenshare: false, canScreenshareAudio: false };
  const sources = perm.canPublishSources ?? [];
  if (sources.length === 0) return { canPublishAudio: true, canPublishVideo: true, canScreenshare: true, canScreenshareAudio: true };
  const allowed = new Set(sources.map((s) => Track.sourceFromProto(s as Parameters<typeof Track.sourceFromProto>[0])));
  return {
    canPublishAudio: allowed.has(Track.Source.Microphone),
    canPublishVideo: allowed.has(Track.Source.Camera),
    canScreenshare: allowed.has(Track.Source.ScreenShare),
    canScreenshareAudio: allowed.has(Track.Source.ScreenShareAudio),
  };
}

/**
 * E2E-Hook `window.__lfVoice` nur im Dev-Server oder in einem ausdrücklich für E2E gebauten Bündel (VITE_E2E=1).
 * `env` defensiv, weil der Controller auch außerhalb von Vite (esbuild-Harness) gebündelt wird.
 */
export function voiceDebugHookEnabled(env: { DEV?: boolean; VITE_E2E?: string } | null | undefined): boolean {
  return !!env && (env.DEV === true || env.VITE_E2E === '1');
}

export function parseParticipantMetadata(raw: string | null | undefined): Partial<VoiceParticipantMetadata> | null {
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as unknown;
    return v && typeof v === 'object' && !Array.isArray(v) ? (v as Partial<VoiceParticipantMetadata>) : null;
  } catch {
    return null;
  }
}

/** Anzeigename: Metadaten.displayName → username → LiveKit-Name → gekürzte ID. */
export function participantDisplayName(identity: string, name: string | undefined, meta: Partial<VoiceParticipantMetadata> | null): string {
  const candidates = [meta?.displayName, meta?.username, name];
  for (const c of candidates) if (typeof c === 'string' && c.trim()) return c.trim();
  return shortUserLabel(identity);
}

export function shortUserLabel(userId: string): string {
  return `Nutzer ${userId.slice(-4)}`;
}

export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const letters = parts.length >= 2 ? `${parts[0]![0]}${parts[1]![0]}` : name.trim().slice(0, 2);
  return letters.toUpperCase() || '?';
}

/** LiveKit-ConnectionQuality (String-Enum) → eigene Stufe. */
export function qualityLevel(q: string | null | undefined): QualityLevel {
  switch (q) {
    case 'excellent':
    case 'good':
    case 'poor':
    case 'lost':
      return q;
    default:
      return 'unknown';
  }
}

export function qualityLabel(level: QualityLevel): string {
  return { excellent: 'Sehr gut', good: 'Gut', poor: 'Schwach', lost: 'Unterbrochen', unknown: 'Unbekannt' }[level];
}

/** Anzahl gefüllter Balken (0–3) der Qualitätsanzeige. */
export function qualityBars(level: QualityLevel): number {
  return { excellent: 3, good: 2, poor: 1, lost: 0, unknown: 0 }[level];
}

/** Ob ein Fehler einer Medienanfrage „vom Nutzer abgebrochen" bedeutet (kein Fehlerhinweis nötig). */
export function isUserCancel(err: unknown): boolean {
  const name = (err as { name?: string } | null)?.name;
  return name === 'NotAllowedError' || name === 'AbortError';
}

/** Verständliche Meldung für Fehler beim Beitreten. */
export function joinErrorMessage(err: unknown): string {
  const status = (err as { status?: number } | null)?.status;
  const code = (err as { code?: string } | null)?.code;
  if (code === 'LIMIT_REACHED') return 'Der Sprachkanal ist voll.';
  if (status === 403 || code === 'MISSING_PERMISSIONS') return 'Dir fehlt die Berechtigung, diesem Sprachkanal beizutreten.';
  if (status === 404) return 'Den Sprachkanal gibt es nicht (mehr).';
  if (status === 503 || code === 'SERVICE_UNAVAILABLE') return 'Der Sprachdienst ist gerade nicht erreichbar.';
  return 'Beitreten fehlgeschlagen. Bitte versuche es erneut.';
}

/** Teilnehmer sortieren: eigene Kachel zuerst, dann alphabetisch. */
export function sortByLocalThenName<T extends { isLocal: boolean; name: string }>(list: T[]): T[] {
  return [...list].sort((a, b) => (a.isLocal === b.isLocal ? a.name.localeCompare(b.name, 'de') : a.isLocal ? -1 : 1));
}
